import type { DiagramNode } from '@civil/schema';
import { across, carriedBy, counterparts, filesBehind, type Counterpart } from '../canvas/crossings.js';
import type { ProjectBundle } from '../project.js';

/**
 * The other side of a selection (prd-deltas §25): from a role in Flow, the pieces of
 * Architecture that carry it and the files behind it, wherever they sit; from a piece,
 * the roles it plays; from a deployable, both. Each is a link to the node itself.
 */

export type GoTo = (view: string, diagram: string, node: string) => void;

const VIEW_NAMES: Record<string, string> = { flow: 'In the flow', architecture: 'In the architecture' };

function List({ title, items, onGoTo }: { title: string; items: Counterpart[]; onGoTo: GoTo }) {
  if (items.length === 0) return null;
  return (
    <div className="crossings">
      <div className="provenance-label muted">{title}</div>
      <ul className="crossings-list">
        {items.slice(0, 12).map((c) => (
          <li key={`${c.diagram}:${c.node.id}`}>
            <button type="button" className="crossing" onClick={() => onGoTo(c.view, c.diagram, c.node.id)}>
              <span className="crossing-name">{c.node.name}</span>
              <span className="crossing-where">
                {c.node.kind}
                {c.diagram !== 'overview' ? ` · ${c.diagramName}` : ''}
                {c.via.parts.length === 0 && c.via.files.length > 0 ? ' · shares files' : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {items.length > 12 ? <p className="muted">and {items.length - 12} more</p> : null}
    </div>
  );
}

/** Below a Flow role or an Architecture piece. */
export function NodeCrossings({
  bundle,
  view,
  node,
  onGoTo,
  onOpenFile,
}: {
  bundle: ProjectBundle;
  view: string;
  node: DiagramNode;
  onGoTo: GoTo;
  onOpenFile?: (path: string, line?: number) => void;
}) {
  const other = across(view);
  const files = filesBehind(bundle, node);
  return (
    <>
      {other ? <List title={VIEW_NAMES[other]!} items={counterparts(bundle, node, other)} onGoTo={onGoTo} /> : null}
      {files.length > 0 ? (
        <div className="crossings">
          <div className="provenance-label muted">Files behind it</div>
          <ul className="repo-files">
            {files.map((file) => (
              <li key={file}>
                {onOpenFile ? (
                  <button type="button" className="repo-file-link" onClick={() => onOpenFile(file)}>
                    <code>{file}</code>
                  </button>
                ) : (
                  <code>{file}</code>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/** Below a deployable: the roles and pieces it helps carry. */
export function PartCrossings({ bundle, part, onGoTo }: { bundle: ProjectBundle; part: string; onGoTo: GoTo }) {
  return (
    <>
      <List title={VIEW_NAMES.flow!} items={carriedBy(bundle, part, 'flow')} onGoTo={onGoTo} />
      <List title={VIEW_NAMES.architecture!} items={carriedBy(bundle, part, 'architecture')} onGoTo={onGoTo} />
    </>
  );
}
